import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/auth-context';
import './support-page.css';

// Splitforms — https://splitforms.com/docs. access_key is a public form
// identifier (meant to sit in client-side code, same as their own docs show
// it), not a secret, so it's fine to source from a REACT_APP_ build-time var.
const SPLITFORMS_ACCESS_KEY = process.env.REACT_APP_SPLITFORMS_ACCESS_KEY;
const SPLITFORMS_SUBJECT    = 'cryptoproscanner-support';

const SECTIONS = [
  {
    title: 'Getting started',
    items: [
      {
        q: 'What does CryptoProScanner actually do?',
        a: 'It scans the market on a schedule and flags coins matching specific technical-analysis setups (strategies) across Weekly, Daily, and 4-hour timeframes — things like a rising 50 SMA with a stretched price, or multiple timeframes agreeing on direction. You see the matches on the Dashboard and on each strategy\'s own page.',
      },
      {
        q: 'How often is data refreshed?',
        a: 'Prices refresh every few minutes; full indicator recomputation across all timeframes runs hourly. Every strategy page shows a "last updated" timestamp.',
      },
      {
        q: 'Are these financial advice?',
        a: 'No. Signals are technical-analysis pattern matches, not trade recommendations. Always do your own research.',
      },
    ],
  },
  {
    title: 'Tiers & billing',
    items: [
      {
        q: 'What\'s the difference between tiers?',
        a: 'Free covers the dashboard, watchlist, and alerts log. Basic adds Telegram alerts for the alignment strategies. Pro and Elite unlock more strategy pages and more Telegram alert options — see the full breakdown on the Pricing page.',
      },
      {
        q: 'Can I change or cancel my plan?',
        a: 'Yes, any time from Settings. Upgrades apply immediately; downgrades and cancellations take effect at the end of your current billing period — you keep access until then.',
      },
      {
        q: 'What payment methods are accepted?',
        a: 'All major credit and debit cards, processed securely through Stripe.',
      },
    ],
  },
  {
    title: 'Telegram alerts',
    items: [
      {
        q: 'How do I connect Telegram?',
        a: 'On the Settings page, click "Connect Telegram," then tap the link it shows you on your phone. That opens the bot in Telegram — press Start there, and Settings will show "✅ Connected" within a few seconds.',
      },
      {
        q: 'I connected but the "Enable" toggle is still off — why?',
        a: 'The enable toggle is separate from connecting your account, so you can pause alerts without disconnecting. Flip it on and click "Save Alert Settings."',
      },
      {
        q: 'I don\'t see a confirmation after tapping the link — is it broken?',
        a: 'It can take a few seconds for the connection to register — the page shows a "Waiting for confirmation…" indicator while it does. If it never resolves, make sure you pressed Start in the Telegram chat (not just opened it), then try disconnecting and reconnecting.',
      },
      {
        q: 'Which strategies can alert me on Telegram?',
        a: 'Only the ones your tier includes — locked strategies show a 🔒 in the Settings checklist. Pick any unlocked strategy and save; you\'ll get a message the moment it fires.',
      },
    ],
  },
];

function SupportForm() {
  const { user } = useAuth();
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus]   = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const loadedAt = useRef(Date.now());

  // Pre-fill from the signed-in account, without clobbering anything the
  // user has already typed (e.g. auth resolves a moment after mount).
  useEffect(() => {
    if (!user) return;
    setName(prev => prev || user.displayName || '');
    setEmail(prev => prev || user.email || '');
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;

    setStatus('sending');
    setErrorMsg('');

    try {
      const res = await fetch('https://splitforms.com/api/submit', {
        method:  'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: SPLITFORMS_ACCESS_KEY,
          subject:    SPLITFORMS_SUBJECT,
          name,
          email,
          message,
          botcheck:       '', // honeypot — real users never fill this in
          form_loaded_at: loadedAt.current,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.success) {
        setStatus('success');
        setName('');
        setEmail(user?.email || '');
        setMessage('');
      } else {
        setStatus('error');
        setErrorMsg(data.message || `Request failed (${res.status})`);
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  if (status === 'success') {
    return (
      <div className="sp-form-success">
        <div className="sp-contact-icon">&#9989;</div>
        <div>
          <div className="sp-contact-title">Message sent</div>
          <div className="sp-contact-sub">Thanks — we'll get back to you by email soon.</div>
        </div>
      </div>
    );
  }

  return (
    <form className="sp-form" onSubmit={handleSubmit}>
      {/* Honeypot — hidden from real users, bots tend to fill every field */}
      <input
        type="text"
        name="botcheck"
        tabIndex="-1"
        autoComplete="off"
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      <div className="sp-form-row">
        <label className="sp-form-label" htmlFor="sp-name">Name</label>
        <input
          id="sp-name"
          className="sp-form-input"
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Your name"
          required
        />
      </div>

      <div className="sp-form-row">
        <label className="sp-form-label" htmlFor="sp-email">Email</label>
        <input
          id="sp-email"
          className="sp-form-input"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
        />
      </div>

      <div className="sp-form-row">
        <label className="sp-form-label" htmlFor="sp-message">Message</label>
        <textarea
          id="sp-message"
          className="sp-form-input sp-form-textarea"
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="How can we help?"
          rows={5}
          required
        />
      </div>

      {status === 'error' && (
        <div className="sp-form-error">
          Something went wrong{errorMsg ? `: ${errorMsg}` : ''}. You can also email{' '}
          <a href="mailto:cryptoproscanner@gmail.com">cryptoproscanner@gmail.com</a> directly.
        </div>
      )}

      <button
        type="submit"
        className={`btn-primary sp-form-submit ${status === 'sending' ? 'saving' : ''}`}
        disabled={status === 'sending'}
      >
        {status === 'sending' ? (
          <>
            <span className="saving-spinner">⟳</span>
            Sending...
          </>
        ) : 'Send message'}
      </button>
    </form>
  );
}

function SupportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="support-page">
      <div className="sp-header">
        <button className="sp-back-btn" onClick={() => navigate(user ? '/dashboard' : '/')}>
          &#8592; {user ? 'Back to Dashboard' : 'Back'}
        </button>
      </div>

      <div className="sp-hero">
        <h1 className="sp-title">Support &amp; FAQ</h1>
        <p className="sp-sub">Answers to common questions. Can't find yours? Reach out below.</p>
      </div>

      {SECTIONS.map(section => (
        <div className="sp-section" key={section.title}>
          <h2 className="sp-section-title">{section.title}</h2>
          <div className="sp-faq-grid">
            {section.items.map(item => (
              <div className="sp-faq-item" key={item.q}>
                <div className="sp-faq-q">{item.q}</div>
                <div className="sp-faq-a">{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="sp-section">
        <h2 className="sp-section-title">Still need help?</h2>
        <SupportForm />
      </div>
    </div>
  );
}

export default SupportPage;
